import { yupResolver } from '@hookform/resolvers/yup'
import { FC, useEffect, useState } from 'react'

import { useForm } from 'react-hook-form'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import s from './Step2.module.scss'

import { getIconByMetaverse } from 'constants/MetaverseIcons'

import { useAppErrorContext } from 'contexts/AppErrorContext'
import { useData } from 'contexts/CreateAdSpotFormContext'

import { adSpotsApi } from 'api/adSpots-api'
import { settingsApi } from 'api/settings-api'

import { AdSpaceIcon } from 'assets/icons/AdSpaceIcon'
import { ImageIcon } from 'assets/icons/ImageIcon'
import { InfoIcon } from 'assets/icons/InfoIcon'
import { LinkIcon } from 'assets/icons/LinkIcon'
import { TagIcon } from 'assets/icons/TagIcon'

import { CheckboxControlled } from 'components/Controls/Checkbox/CheckboxControlled'
import { CustomDropzone } from 'components/Controls/CustomDropzone/CustomDropzone'
import { InputControlled } from 'components/Controls/Input/InputControlled'
import { TextareaControlled } from 'components/Controls/Textarea/TextareaControlled'
import { CustomButton } from 'components/UI/CustomButton/CustomButton'
import { ModalFullScreen } from 'components/UI/ModalFullScreen/ModalFullScreen'
import CustomTooltip from 'components/UI/Tooltip'

import { makeAdSpaceSchema } from 'pages/MyAdSpots/validation'

interface Step2Props {
  onHide: () => void
}

interface FormType {
  name: string
  description: string
  externalLink: string
  moderationEnabled: boolean
  files: File[]
  defaultFiles: File[]
  robloxImgUrl: string
  jumpUrl: string
  unityUUID: string
}

const Step2: FC<Step2Props> = ({ onHide }) => {
  const [searchParams] = useSearchParams()
  const { id } = useParams()
  const { data } = useData()
  const { setAppError } = useAppErrorContext()

  const [loading, setLoading] = useState(false)

  const navigate = useNavigate()

  const {
    handleSubmit,
    control,
    formState: { errors },
    setFocus,
    reset,
    setValue,
    watch,
  } = useForm<FormType>({
    mode: 'onBlur',
    resolver: yupResolver(makeAdSpaceSchema(data.metaverseType, +(id || 0))),
    defaultValues: {
      name: '',
      description: '',
      externalLink: '',
      moderationEnabled: false,
      files: [] as File[],
      defaultFiles: [] as File[],
      robloxImgUrl: '',
      jumpUrl: '',
      unityUUID: '',
    },
  })

  const defFile = watch('defaultFiles')
  const robloxImgUrl = watch('robloxImgUrl')

  useEffect(() => {
    setFocus('name')
  }, [])

  //edit reset
  const [thumb, setThumb] = useState('')
  const [defThumb, setDefThumb] = useState('')

  useEffect(() => {
    const setDataToForm = async (id: string) => {
      const res = await adSpotsApi.getOne(+(id || 0))
      const modType = await adSpotsApi.getModType(+id)
      const adSpot = res.data
      setThumb(adSpot.preview_url)
      setDefThumb(adSpot.default_media || '')
      reset({
        name: adSpot?.name || '',
        description: adSpot?.description || '',
        moderationEnabled: !modType.data.auto_approval,
        externalLink: adSpot?.default_external_link || '',
        jumpUrl: adSpot?.jump_url || '',
      })
    }
    if (id) {
      setLoading(true)
      setDataToForm(id).then(() => setLoading(false))
    }
  }, [])

  useEffect(() => {
    if (!id) {
      setLoading(true)
      settingsApi.get().then((res) => {
        setValue('moderationEnabled', !!res.data.user.moderation_policy_type || false)
        setLoading(false)
      })
    }
  }, [])

  const updateAdSpot = async (fData: FormType) => {
    if (id) {
      setLoading(true)
      const formData = new FormData()
      formData.append(
        'data',
        JSON.stringify({
          name: fData.name,
          description: fData.description,
          auto_approval: !fData.moderationEnabled,
          id: +id,
          jump_url: fData.jumpUrl || null,
          default_external_link: fData.externalLink || null,
        })
      )
      formData.append('preview_file', fData?.files?.[0])
      try {
        await adSpotsApi.update(+id, formData)
      } catch (e) {
        setAppError(e)
      }
    }
    localStorage.setItem('adSpaceEdit', 'true')
    setLoading(false)
    navigate(`../?${searchParams}`)
  }

  const createAdSpot = async (fData: FormType) => {
    setLoading(true)
    try {
      const formData = new FormData()
      let fields: any = {
        name: fData.name,
        description: fData.description,
        auto_approval: !fData.moderationEnabled,
        metaverse: data.metaverseType,
        jump_url: fData.jumpUrl || null,
        asset_type: data.metaverseType === 'Decentraland' ? data?.type?.value || 0 : null,
        default_external_link: fData.externalLink || null,
        uuid: data.metaverseType === 'Voxels' ? data?.uuid : data.metaverseType === 'Unity' ? fData?.unityUUID : null,
      }

      if (data?.metaverseType === 'Roblox') {
        fields = {
          ...fields,
          default_media: fData?.robloxImgUrl || null,
        }
      } else {
        formData.append('default_media', fData?.defaultFiles?.length ? fData.defaultFiles[0] : JSON.stringify(null))
      }

      formData.append('data', JSON.stringify(fields))
      formData.append('preview_file', fData.files[0])

      const res = await adSpotsApi.create(formData)
      if (res.status === 200) {
        const adSpotId = res.data.id
        const pin = res?.data?.pin
        if (!adSpotId) {
          localStorage.setItem('adSpotId', '')
          console.error('adSpotId - is not returned from server')
        }
        if (pin) localStorage.setItem('pin', pin)
        localStorage.setItem('adSpotCreated', 'true')
        localStorage.setItem('adSpotId', adSpotId)
        navigate(`../?${searchParams}`, { replace: true })
      } else {
        setAppError({ error: res.data?.msg })
      }
      return
    } catch (e: any) {
      setLoading(false)
      setAppError(e)
      navigate(`../?${searchParams}`, { replace: true })
      return
    }
  }

  const onSubmit = (fData: FormType) => {
    if (id) {
      updateAdSpot(fData)
    } else {
      createAdSpot(fData)
    }
  }

  return (
    <ModalFullScreen onHide={onHide} loading={loading} className={s.modalWrapper} testid="createAdSpotStep2">
      <div className={s.headerWrapper}>
        <h1 className={s.header}>{id ? 'Edit' : 'Create New'} Ad Space</h1>
        {!id &&
          (data.metaverseType === 'Decentraland' ? (
            <div className={s.prevStepInfo}>
              <img src={getIconByMetaverse('Decentraland')} alt="icon" />
              <div>
                {data.type?.label} for Decentraland <br />
              </div>
            </div>
          ) : (
            <div className={s.prevStepInfo}>
              <img src={getIconByMetaverse(data.metaverseType)} alt="icon" />
              <div data-testid="meta-type">{data.metaverseType}</div>
            </div>
          ))}
      </div>
      <form onSubmit={handleSubmit(onSubmit)} className={s.formWrapper} id="step2adspotForm">
        <div className={s.formFieldsWrapper}>
          <div className={s.inputsWrapper}>
            <InputControlled
              icon={<TagIcon />}
              name="name"
              placeholder="Enter name..."
              error={errors?.name?.message}
              control={control}
            />
            {data.metaverseType === 'Unity' && (
              <InputControlled
                icon={<AdSpaceIcon />}
                name="unityUUID"
                placeholder="Enter Ad Space UUID..."
                error={errors?.unityUUID?.message}
                control={control}
              />
            )}
            {data.metaverseType === 'Roblox' && (
              <div className={s.robloxDefaultWrapper}>
                <InputControlled
                  icon={<ImageIcon />}
                  name="robloxImgUrl"
                  placeholder="Enter Roblox default image..."
                  error={errors?.robloxImgUrl?.message}
                  control={control}
                />
                <CustomTooltip title="Please, enter a link like rbxassetid://{your_image_id} or http://www.roblox.com/asset/?id={your_image_id}">
                  <InfoIcon className={s.rbxInfoIcon} />
                </CustomTooltip>
              </div>
            )}
            {!id &&
              (defFile?.length || (data.metaverseType === 'Roblox' && robloxImgUrl?.length) ? (
                <InputControlled
                  icon={<LinkIcon />}
                  name="externalLink"
                  placeholder={
                    data.metaverseType === 'Roblox' ? 'Enter Place ID for teleport...' : 'External target link...'
                  }
                  error={errors?.externalLink?.message}
                  disabled={
                    data.metaverseType === 'Roblox' ? !robloxImgUrl?.length : !defFile?.length && !defThumb?.length
                  }
                  control={control}
                />
              ) : (
                <CustomTooltip
                  title={`${
                    data.metaverseType === 'Roblox'
                      ? 'Please, enter default image link firstly'
                      : 'Please, add a default image firstly'
                  }`}
                >
                  <InputControlled
                    icon={<LinkIcon />}
                    name="externalLink"
                    placeholder={
                      data.metaverseType === 'Roblox' ? 'Enter Place ID for teleport...' : 'External target link...'
                    }
                    error={errors?.externalLink?.message}
                    disabled={
                      data.metaverseType === 'Roblox' ? !robloxImgUrl?.length : !defFile?.length && !defThumb?.length
                    }
                    control={control}
                  />
                </CustomTooltip>
              ))}
            <TextareaControlled
              icon={<InfoIcon />}
              control={control}
              name="description"
              placeholder="Describe the ad space..."
              height={152}
              error={errors?.description?.message}
            />
            <InputControlled
              icon={<LinkIcon />}
              name="jumpUrl"
              placeholder="Enter jump URL..."
              error={errors?.jumpUrl?.message}
              control={control}
            />
          </div>
          <div className={s.filesAndCheckboxesWrapper}>
            <div className={s.filesWrapper}>
              <CustomDropzone
                control={control}
                name="files"
                uploaded={thumb}
                noMediaTitle={<div className={s.dropzoneTitle}>Upload Preview Image in .jpg .png up to 2 MB</div>}
                // @ts-ignore
                error={errors?.files?.message}
                wrapperStyles={{ maxWidth: 192, width: '100%',}}
                dropzoneStyles={{ height: 128, maxWidth: 192, width: '100%', borderRadius: 8 }}
                iconWidth={40}
                iconHeight={40}
              />
              {data.metaverseType !== 'Roblox' && !id && (
                <CustomDropzone
                  control={control}
                  name="defaultFiles"
                  uploaded={defThumb}
                  noMediaTitle={<div className={s.dropzoneTitle}>Upload Default Ad Image in .jpg .png up to 2 MB</div>}
                  // @ts-ignore
                  error={errors?.defaultFiles?.message}
                  wrapperStyles={{ maxWidth: 192, width: '100%',}}
                  dropzoneStyles={{ height: 128, maxWidth: 192, width: '100%', borderRadius: 8 }}
                  iconWidth={40}
                  iconHeight={40}
                />
              )}
            </div>
            <div className={s.checkBoxesWrapper}>
              <CheckboxControlled
                color="secondary"
                control={control}
                name="moderationEnabled"
                labelText="Moderation enabled"
              />
            </div>
          </div>
        </div>
      </form>
      <div className={s.buttonsWrapper}>
        <CustomButton type="submit" variant="secondary" size="240" form="step2adspotForm" testid="save-btn" className={s.saveBtn}>
          Save
        </CustomButton>
        <CustomButton type="button" variant="outlined" onClick={onHide} size="152">
          Close
        </CustomButton>
      </div>
    </ModalFullScreen>
  )
}

export default Step2
